import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BasePromptEditForm } from "@/components/ui/BasePromptEditForm";
import { routes } from "@/configs/router.config/routes";
import { useCommand } from "@/hooks/agent/useCommand";
import { useCommandMutations } from "@/hooks/agent/useCommandMutations";
import { useWorkspaces } from "@/hooks/useWorkspaces";

const CommandFormPage: React.FC = () => {
  const { workspaceId, commandId } = useParams<{ workspaceId: string; commandId?: string; }>();
  const navigate = useNavigate();
  const { workspaces } = useWorkspaces();
  const [searchParams] = useSearchParams();

  const { command } = useCommand(commandId || null);
  const { createCommand, updateCommand, isCreating, isUpdating } = useCommandMutations();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("all");
  const [selectedUserScope, setSelectedUserScope] = useState<string>("all");
  const isEdit = !!commandId;

  const workspaceOptions = useMemo(
    () => [
      { value: "all", label: "All Workspaces" },
      ...(workspaces ? workspaces.map((ws) => ({ value: ws.id, label: ws.name })) : []),
    ],
    [workspaces],
  );

  const userScopeOptions = [
    { value: "all", label: "全ユーザー" },
    { value: "personal", label: "自分のみ" },
  ];

  useEffect(() => {
    if (command) {
      setName(command.name);
      setDescription(command.description);
      setInstruction(command.instruction);
      setEnabled(command.enabled);
      setSelectedWorkspaceId(
        !command.workspaceId || command.workspaceId === "None" ? "all" : command.workspaceId,
      );
      setSelectedUserScope(command.userId ? "personal" : "all");
    }
  }, [command]);

  const handleSubmit = async () => {
    if (!workspaceId) return;

    const requestData = {
      name,
      description,
      instruction,
      scope_workspace: selectedWorkspaceId === "all" ? "all_workspace" : selectedWorkspaceId as "all" | "all_workspace",
      scope_user: selectedUserScope === "all" ? "all_user" : "self_only" as "all_user" | "self_only",
    };

    if (isEdit && commandId) {
      await updateCommand(commandId, {
        ...requestData,
        enabled,
      });
    } else {
      await createCommand(requestData);
    }

    // タブパラメータを保持して戻る
    const tab = searchParams.get("tab") || "commands";
    navigate(`${routes.agentManagement(workspaceId)}?tab=${tab}`);
  };

  const handleCancel = () => {
    if (!workspaceId) return;
    const tab = searchParams.get("tab") || "commands";
    navigate(`${routes.agentManagement(workspaceId)}?tab=${tab}`);
  };

  if (!workspaceId) return null;

  return (
    <BasePromptEditForm
      title={isEdit ? "プロンプトを編集" : "プロンプトを作成"}
      name={name}
      setName={setName}
      description={description}
      setDescription={setDescription}
      instruction={instruction}
      setInstruction={setInstruction}
      enabled={enabled}
      setEnabled={setEnabled}
      selectedWorkspaceId={selectedWorkspaceId}
      setSelectedWorkspaceId={setSelectedWorkspaceId}
      selectedUserScope={selectedUserScope}
      setSelectedUserScope={setSelectedUserScope}
      workspaceOptions={workspaceOptions}
      userScopeOptions={userScopeOptions}
      isSaving={isCreating || isUpdating}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      namePlaceholder="Enter command name"
    />
  );
};

export default CommandFormPage;
